import { useEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useSession } from "../auth/session";
import { icons } from "../company-ui";
import { currentItem, groupOfItem, hrefOf, menuFor, type GroupId, type NavGroup, type NavItem } from "./nav";
import { readRail, writeRail } from "./rail-memory";

/**
 * The menu (ALN-25, app-shell.md; mockup v24 menu, v32 collapse). Over 900px: the rail of groups,
 * each with its name and description, ending with Collapse menu; collapsed, a 64px strip of group
 * icons with tooltips and flyouts. At 900px and under: a row of sections and a row of the chosen
 * section's pages, the same collapsed or not. Only pages the person's roles can open are listed.
 */

/** Groups the person opened or closed in the rail, kept while the page is open (the shell remounts per route). */
const toggledGroups: Record<string, boolean> = {};
const FOCUSABLE = "a, button, input, select, textarea, summary, [tabindex]:not([tabindex='-1'])";

export function AppNav() {
  const { data } = useSession();
  const [location] = useLocation();
  const search = useSearch();
  const menu = useMemo(() => menuFor(data), [data]);
  const current = currentItem(location, search ? `?${search}` : "");
  const curGroup = groupOfItem(current)?.id;
  const tenant = data?.tenant?.id, user = data?.user?.id;

  const [collapsed, setCollapsedState] = useState(() => readRail(tenant, user));
  useEffect(() => { setCollapsedState(readRail(tenant, user)); }, [tenant, user]);
  const [, rerender] = useState(0);
  const [fly, setFly] = useState<GroupId | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const [mob, setMob] = useState<GroupId | null>(null);
  const focusFirst = useRef<GroupId | null>(null);
  const nav = useRef<HTMLElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  const setCollapsed = (c: boolean) => {
    setCollapsedState(c);
    writeRail(tenant, user, c);
    setFly(null);
    setTip(null);
    toggle.current?.focus();
  };
  const isOpen = (id: GroupId) => toggledGroups[id] ?? id === curGroup;
  const iconOf = (id: GroupId) => nav.current?.querySelector<HTMLButtonElement>(`#rail-btn-${id}`);
  const flyLinks = (id: GroupId) => [...(nav.current?.querySelectorAll<HTMLElement>(`#rail-fly-${id} a`) ?? [])];
  const closeFly = (refocus: boolean) => {
    const id = fly;
    setFly(null);
    if (refocus && id) iconOf(id)?.focus();
  };

  // ArrowDown on an icon opens its flyout and focuses its first page, once it has rendered.
  useEffect(() => {
    if (fly && focusFirst.current === fly) flyLinks(fly)[0]?.focus();
    focusFirst.current = null;
  });

  // While a flyout is open: Escape closes it (focus back on its icon); a click outside closes it,
  // and focus goes back to the icon unless the click landed on another control.
  useEffect(() => {
    if (!fly) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closeFly(true);
    };
    const away = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest?.(`#rail-item-${fly}`)) return;
      const control = t.closest?.(FOCUSABLE);
      if (!control) e.preventDefault(); // keep focus off the page, so it can go back to the icon
      closeFly(!control);
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", away, true);
    return () => { document.removeEventListener("keydown", onKey, true); document.removeEventListener("mousedown", away, true); };
  });

  // On a phone, keep the current page and its section in view, sideways in their rows only: scrollIntoView
  // would also scroll the page up to the menu whenever the query changes (a stage tab, a filter).
  useEffect(() => {
    if (!window.matchMedia("(max-width: 900px)").matches) return;
    centreInRow(nav.current?.querySelector<HTMLElement>(".navmob__sub a[aria-current=\"page\"]"));
    centreInRow(nav.current?.querySelector<HTMLElement>(".navmob__sec[aria-expanded=\"true\"]"));
  }, [location, search]);

  const link = (i: NavItem, onPick?: () => void) => (
    <Link key={hrefOf(i)} href={hrefOf(i)} aria-current={i === current ? "page" : undefined} onClick={onPick}>{i.label}</Link>
  );

  const onIconKey = (g: NavGroup) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusFirst.current = g.id;
      setTip(null);
      if (fly === g.id) flyLinks(g.id)[0]?.focus();
      else setFly(g.id);
    } else if (e.key === "Escape" && !fly && tip) {
      setTip(null);
    }
  };
  const onFlyKey = (g: NavGroup) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const ls = flyLinks(g.id);
    const i = ls.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Home") return ls[0]?.focus();
    if (e.key === "End") return ls[ls.length - 1]?.focus();
    if (e.key === "ArrowUp" && i <= 0) return iconOf(g.id)?.focus();
    ls[Math.max(0, Math.min(ls.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)))]?.focus();
  };
  // Tabbing out of an open flyout closes it.
  const onItemBlur = (e: FocusEvent<HTMLLIElement>) => {
    if (fly && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) setFly(null);
  };
  const showTip = (id: string) => { if (fly !== id) setTip(id); };
  const hideTip = (e: { currentTarget: HTMLElement }) => { if (document.activeElement !== e.currentTarget) setTip(null); };

  const groups = menu.flatMap(b => b.groups);
  const mobGroup = groups.find(g => g.id === mob) ?? groups.find(g => g.id === curGroup) ?? groups[0];

  return (
    <nav className={`appnav${collapsed ? " appnav--collapsed" : ""}`} aria-label="Avero Vintner" ref={nav}>
      <div className="navdesk" id="appnav-rail">
        {collapsed ? (
          <>
            <span className="sr" id="rail-current">Current section</span>
            <ul className="rail__list" aria-label="Sections">
              {menu.flatMap((b, bi) => b.groups.map((g, gi) => {
                const open = fly === g.id;
                const cur = g.id === curGroup;
                return (
                  <li key={g.id} id={`rail-item-${g.id}`} className={`rail__item${cur ? " is-current" : ""}${open ? " is-open" : ""}${bi && !gi ? " is-band" : ""}`} onBlur={onItemBlur}>
                    <button
                      type="button" className="rail__btn" id={`rail-btn-${g.id}`}
                      aria-labelledby={`rail-tip-${g.id}`} aria-describedby={cur ? "rail-current" : undefined}
                      aria-expanded={open} aria-controls={`rail-fly-${g.id}`}
                      onClick={e => { setTip(null); setFly(open ? null : g.id); e.currentTarget.focus(); }}
                      onKeyDown={onIconKey(g)}
                      onMouseEnter={() => showTip(g.id)} onMouseLeave={hideTip}
                      onFocus={() => showTip(g.id)} onBlur={() => setTip(null)}
                    >{icons[g.icon]}</button>
                    <span className="rail__tip" id={`rail-tip-${g.id}`} role="tooltip" hidden={tip !== g.id || open}>{g.label}</span>
                    <div className="rail__fly" id={`rail-fly-${g.id}`} role="group" aria-labelledby={`rail-flyh-${g.id}`} hidden={!open} onKeyDown={onFlyKey(g)}>
                      <p className="rail__flyh" id={`rail-flyh-${g.id}`}><b>{g.label}</b>{" "}<span>{g.hint}</span></p>
                      <div className="rail__links">{g.items.map(i => link(i, () => setFly(null)))}</div>
                    </div>
                  </li>
                );
              }))}
            </ul>
          </>
        ) : (
          menu.map(b => (
            <div className={`navband navband--${b.id}`} key={b.id}>
              {b.label && <div className="appnav__label">{b.label}</div>}
              {b.groups.map(g => {
                const open = isOpen(g.id);
                return (
                  <div className={`navsec${g.id === curGroup ? " is-current" : ""}`} key={g.id}>
                    <button type="button" className="navsec__h" aria-expanded={open} aria-controls={`navsec-${g.id}`} onClick={() => { toggledGroups[g.id] = !open; rerender(n => n + 1); }}>
                      {icons[g.icon]}
                      <span className="navsec__t">{g.label}{" "}<small>{g.hint}</small></span>
                      <span className="navsec__chev">{icons.chevron}</span>
                    </button>
                    <div className="navsec__list" id={`navsec-${g.id}`} hidden={!open}>{g.items.map(i => link(i))}</div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div className="railfoot">
          <div className={collapsed ? "rail__item" : "railfoot__wrap"}>
            <button
              ref={toggle} type="button" className={collapsed ? "rail__btn" : "railtoggle"}
              aria-expanded={!collapsed} aria-controls="appnav-rail"
              aria-labelledby={collapsed ? "rail-tip-toggle" : undefined}
              onClick={() => setCollapsed(!collapsed)}
              onMouseEnter={() => collapsed && setTip("toggle")} onMouseLeave={hideTip}
              onFocus={() => collapsed && setTip("toggle")} onBlur={() => setTip(null)}
              onKeyDown={e => { if (e.key === "Escape" && tip) setTip(null); }}
            >
              {collapsed ? icons.railExpand : icons.railCollapse}
              {!collapsed && <span>Collapse menu</span>}
            </button>
            {collapsed && <span className="rail__tip" id="rail-tip-toggle" role="tooltip" hidden={tip !== "toggle"}>Expand menu</span>}
          </div>
        </div>
      </div>
      {mobGroup && (
        <div className="navmob">
          <div className="navmob__row" role="group" aria-label="Sections">
            {groups.map(g => (
              <button key={g.id} type="button" className="navmob__sec" aria-expanded={g.id === mobGroup.id} aria-controls="navmob-sub" onClick={() => setMob(g.id)}>
                {icons[g.icon]}{g.label}
              </button>
            ))}
          </div>
          <div className="navmob__row navmob__sub" id="navmob-sub" role="group" aria-label={mobGroup.label}>
            {mobGroup.items.map(i => link(i))}
          </div>
        </div>
      )}
    </nav>
  );
}

/** Scrolls an item to the middle of its own sideways row, without moving the page. */
function centreInRow(el: HTMLElement | null | undefined) {
  const row = el?.parentElement;
  if (!el || !row) return;
  const left = row.scrollLeft + el.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - el.offsetWidth) / 2;
  row.scrollLeft = Math.max(0, Math.min(left, row.scrollWidth - row.clientWidth));
}
